import { PageController } from '@client/components/_core/layout/PageController';
import { RichText } from '@client/components/_ui/display/RichText';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { firstQueryError, isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Collapse, Input, Select, Tag } from 'antd';
import clsx from 'clsx';
import { ChevronRight, HelpCircle, Search } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';

// react-player ships adapters for HLS (~496 KB), DASH (~940 KB), MUX (~504 KB)
// and 7 other platforms. Lazy-load so the FAQ route chunk doesn't ship them
// to every user who navigates here — they're only fetched when a video card
// actually renders.
const ReactPlayer = lazy(() => import('react-player'));

export const Route = createFileRoute('/console/$orgId/faq')({
    staticData: {
        breadcrumb: {
            title: 'FAQ',
        },
        ui: {
            title: 'Frequently asked questions',
            subtitle: 'Find answers to common questions about using the platform',
            icon: HelpCircle,
        },
        roles: ['user', 'shared_edit_user', 'org_admin', 'training_manager'],
    },
    component: FaqPage,
});

function VideoPlayer({ videoFileKey }: { videoFileKey?: string }) {
    const { data, isLoading } = trpc.file.getDownloadUrl.useQuery(
        videoFileKey
            ? {
                  key: videoFileKey,
              }
            : skipToken,
    );

    if (!videoFileKey) {
        return null;
    }

    return (
        <Loading spinning={isLoading} size="large" description="Loading video..." keepWrapper>
            <div className="mt-6 flex justify-center">
                <div className="min-h-20 max-w-[640px] overflow-hidden rounded-lg shadow-lg">
                    <Suspense fallback={null}>
                        <ReactPlayer src={data} controls width="100%" height="100%" />
                    </Suspense>
                </div>
            </div>
        </Loading>
    );
}

function FaqPage() {
    const [searchValue, setSearchValue] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    const input = useMemo<RouterInputs['faq']['listFaqs']>(() => {
        const where: { AND: NonNullable<RouterInputs['faq']['listFaqs']['where']>[] } = { AND: [] };

        if (selectedCategories.length > 0) {
            where.AND.push({
                OR: selectedCategories.map((category) => ({
                    categories: {
                        has: category,
                    },
                })),
            });
        }

        if (searchValue.trim()) {
            where.AND.push({
                OR: [
                    { title: { contains: searchValue.trim(), mode: 'insensitive' } },
                    { content: { contains: searchValue.trim(), mode: 'insensitive' } },
                ],
            });
        }

        return {
            limit: 100,
            offset: 0,
            where: where.AND.length > 0 ? where : undefined,
        };
    }, [searchValue, selectedCategories]);

    const faqsQuery = trpc.faq.listFaqs.useQuery(input);
    const categoriesQuery = trpc.faq.getFaqCategories.useQuery();
    const { data: faqs, isPending } = faqsQuery;
    const categories = categoriesQuery.data;
    const faqQueries = [faqsQuery, categoriesQuery];
    const queryError = firstQueryError(faqQueries);

    const collapseItems = useMemo(() => {
        return (faqs?.rows ?? []).map((faq) => ({
            key: String(faq.id),
            label: (
                <div className="flex flex-col gap-2 py-1">
                    <span className="font-medium text-base text-foreground">{faq.title}</span>
                    {faq.categories && faq.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {faq.categories.map((category: string) => (
                                <Tag
                                    key={category}
                                    className="m-0 rounded-full border-0 bg-accent-50 px-2.5 py-0.5 text-accent-700 text-xs"
                                >
                                    {category}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>
            ),
            children: (
                <div className="bg-muted px-6 py-5">
                    <RichText data={faq.content} className="prose prose-sm max-w-none" format="tiptap" />
                    <VideoPlayer videoFileKey={faq.videoFileKey || ''} />
                </div>
            ),
        }));
    }, [faqs?.rows]);

    const handleExpandChange = (keys: string | string[]) => {
        setExpandedKeys(Array.isArray(keys) ? keys : [keys]);
    };

    const hasResults = (faqs?.rows?.length ?? 0) > 0;
    const hasFilters = searchValue.trim() !== '' || selectedCategories.length > 0;

    return (
        <PageController route={Route}>
            {queryError ? (
                <InlineQueryError
                    error={queryError}
                    onRetry={() => retryFailedQueries(faqQueries)}
                    retrying={isRetryingQueries(faqQueries)}
                />
            ) : (
                <>
                    <div className="mx-auto max-w-5xl px-4 py-6">
                        {/* Search and Filter Section */}
                        <div className="mb-6 flex flex-col gap-4 sm:flex-row">
                            <Input
                                placeholder="Search FAQs..."
                                prefix={<Search className="h-4 w-4 text-subtle-foreground" />}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                allowClear
                                size="large"
                                className="flex-1"
                            />
                            <Select
                                mode="multiple"
                                placeholder="Filter by category"
                                value={selectedCategories}
                                onChange={setSelectedCategories}
                                options={categories?.map((category) => ({
                                    label: category,
                                    value: category,
                                }))}
                                allowClear
                                size="large"
                                className="w-full sm:w-64"
                                maxTagCount="responsive"
                            />
                        </div>

                        {/* FAQ List */}
                        <Loading spinning={isPending} keepWrapper>
                            {hasResults ? (
                                <Collapse
                                    accordion
                                    activeKey={expandedKeys}
                                    onChange={handleExpandChange}
                                    bordered={false}
                                    expandIcon={({ isActive }) => (
                                        <ChevronRight
                                            className={clsx(
                                                'h-5 w-5 text-subtle-foreground transition-transform duration-200',
                                                isActive && 'rotate-90',
                                            )}
                                        />
                                    )}
                                    className={clsx(
                                        'bg-transparent',
                                        '[&_.ant-collapse-item]:mb-3 [&_.ant-collapse-item]:overflow-hidden [&_.ant-collapse-item]:rounded-lg [&_.ant-collapse-item]:border [&_.ant-collapse-item]:border-separator [&_.ant-collapse-item]:bg-card',
                                        '[&_.ant-collapse-item:last-child]:mb-0',
                                        '[&_.ant-collapse-header]:items-start [&_.ant-collapse-header]:px-5 [&_.ant-collapse-header]:py-4',
                                        '[&_.ant-collapse-header:hover]:bg-muted',
                                        '[&_.ant-collapse-expand-icon]:mt-1.5',
                                        '[&_.ant-collapse-content]:border-separator [&_.ant-collapse-content]:border-t',
                                        '[&_.ant-collapse-content-box]:p-0',
                                        '[&_.ant-collapse-item-active]:border-accent-200 [&_.ant-collapse-item-active]:ring-1 [&_.ant-collapse-item-active]:ring-accent-100',
                                    )}
                                    items={collapseItems}
                                />
                            ) : (
                                !isPending && (
                                    <EmptyState
                                        icon={<HelpCircle />}
                                        title="No FAQs available at the moment"
                                        hasFilters={hasFilters}
                                        noMatchesTitle="No FAQs match your search criteria"
                                        onClearFilters={() => {
                                            setSearchValue('');
                                            setSelectedCategories([]);
                                        }}
                                    />
                                )
                            )}
                        </Loading>

                        {/* Results count */}
                        {hasResults && (
                            <div className="mt-4 text-center text-muted-foreground text-sm">
                                Showing {faqs?.rows?.length ?? 0} of {faqs?.total ?? 0} questions
                            </div>
                        )}
                    </div>
                </>
            )}
        </PageController>
    );
}
